import { cn } from "@/lib/utils";

/**
 * Per-club identity chip: a rounded square in a deterministic brand colour with the club's
 * initials. Unlike {@link Avatar} (a person's gradient-ink photo/initials), this keys its colour
 * off the club so the same club always reads the same across the app — list, switcher, threads.
 */

const AVATAR_PALETTE = [
  "oklch(0.52 0.155 256)",
  "oklch(0.56 0.12 158)",
  "oklch(0.6 0.13 40)",
  "oklch(0.5 0.14 300)",
  "oklch(0.55 0.12 220)",
  "oklch(0.58 0.13 95)",
] as const;

export interface ClubAvatarProps {
  /** Club display name — drives the initials and, by default, the colour. */
  name: string;
  /** Stable colour seed; pass the club id so the colour survives a rename. Defaults to the name. */
  seed?: string;
  /** Larger 40px variant (default is 28px). */
  lg?: boolean;
  className?: string;
}

function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function colorOf(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

export function ClubAvatar({ name, seed, lg, className }: ClubAvatarProps) {
  return (
    <span
      className={cn("club-avatar", lg && "lg", className)}
      style={{ background: colorOf(seed ?? name) }}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </span>
  );
}
